import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { of } from 'rxjs/observable/of';
import { catchError, filter, map, pluck, takeUntil, take, switchMap, tap, retry } from 'rxjs/operators';
import { sortBy as _sortBy, get } from 'lodash';
import * as jmespath from 'jmespath';
import { ResourceRestService } from '@ispm/workflow/services/resource-rest.service';
import { Resources } from '@ispm/workflow/constants/resources.enum';
import { modalResponse } from '@ispm/layout/constants/modal-responses.enum';
import { ConfirmModalComponent } from '@ispm/layout/components/modals/confirm-modal/confirm-modal.component';
import { ConfirmModalLoaderPayload } from '@ispm/layout/classes/confirm-modal-loader-payload';
import { ModalTypes } from '@ispm/layout/components/modals/contants/modal-types.enum';
import { DynamicModalLoaderService } from '@ispm/layout/services/dynamic-modal-loader.service';
import { IModalConfiguration } from '@ispm/shared/interfaces/action.interface';
import { ItherapyMatch } from '@ispm/therapies/interfaces/therapy-match.interface';
import { DynamicSearchService } from '../../worklist/services/dynamic-search.service';
import { DynamicSearchPayload } from '@ispm-shared/modules/common/dynamic-search';
import { ConditionalItem, IConditionalItem } from '@ispm/shared/classes/conditional-item.class';
import { WorkflowService } from '@ispm/workflow/services/workflow.service';
import { Store } from '@ngrx/store';
import { EMPTY } from 'rxjs';
import { currentOrderIsNewOrderSelector } from '@ispm/dashboard/test/store/current-order.selector';
import { StringUtils } from '@ispm/shared/utils/String.utils';
import { IExtendedTab } from '../interfaces/section-configuration.interface';
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';
import {TunnelResult} from "@ispm/layout/classes/tunnel-result";
import {EmitType} from "@ispm/layout/constants/emit-types.enum";
import {EmitterTypes} from "@ispm/layout/constants/emitter-types.enum";
import {WorkflowConfigService} from "@ispm/workflow/services/workflow-config.service";
import {combineLatest} from "rxjs/observable/combineLatest";

interface IResourceWithBioMarkers {
  patientData: { patientBiomarkers: {
      aberrationType?: string;
      AberrationType?: string;
      aberrationClass?: string;
      AberrationClass?: string;
      gene: string;
    }[]};
  _aggregatedChildren: any[];
}

@Injectable()
export class TherapiesService {

  constructor(
    private _http: ResourceRestService,
    private _dynamicModalLoader: DynamicModalLoaderService,
    private _dynamicSearchService: DynamicSearchService,
    private _workflowService: WorkflowService,
    private _store: Store<any>,
    private notificationMessageService: NotificationMessageService,
    private workflowConfig: WorkflowConfigService,
  ) { }

  public static ModalLoaderTunnelName = 'TREATMENT_MODAL';
  static REQUIRED_REVIEWS = 0;
  static clinicalRecommendationsURLSuffix = '/clinicalRecommendation';
  static therapiesURLSuffix = '/therapyMatch';
  static treatmentsURLSuffix = '/TreatmentPlan';
  static trialsURLSuffix = '/clinicalTrialMatch';
  static reflexMessageForUi = '/reflexMessageForUi';
  static opinionURLSuffix = 'workflow/getOpinions';
  static updateReportOpinionURLSuffix = 'workflow/updateReportInclusionOpinion';
  static updateMIOpinionURLSuffix = 'workflow/updateMIInclusionOpinion';

  public getOpinion() {
    return this._store.select(currentOrderIsNewOrderSelector)
      .pipe(
        take(1),
        switchMap((isNewOrder) => isNewOrder
          ? of(EMPTY)
          : this._http.get(TherapiesService.opinionURLSuffix, {
              revisionId: ResourceRestService.getResourcesString(Resources.RequestedRevision)
            })
            .pipe(
              map(res => res.json()),
              catchError(() => {
                return EMPTY;
              })
            )
        )
      );
  }

  public getTherapies(allTherapies, aberrationId?: string) {
    const params: any = {
      context: ResourceRestService.getResourcesString(Resources.TherapyMatch, Resources.RequestedRevision),
      allTherapies: Number(allTherapies)
    };

    if (aberrationId) {
      params.aberrationId = aberrationId;
    }

    return this._http.get(TherapiesService.therapiesURLSuffix, params, false).pipe(
      map(res => res.json()),
      catchError(err => {
        this.notificationMessageService.error(err.error || 'unable to get therapies');
        return Observable.empty();
      })
    );
  }

  public getTreatments() {
    const params: any = {
      context: ResourceRestService.getResourcesString(Resources.TreatmentPlan, Resources.RequestedRevision),
      '_sort': 'order'
    };

    return this._http.get(TherapiesService.treatmentsURLSuffix, params, false)
      .map(res => res.json())
      .catch(err => {
        this.notificationMessageService.error(err.error || 'unable to get treatments');
        return Observable.throw(err);
      });
  }

  public updateVisibilityOpinion(visibleIn: 'report' | 'MI', updateId, visible: boolean) {
    const url = visibleIn === 'report' ? TherapiesService.updateReportOpinionURLSuffix : TherapiesService.updateMIOpinionURLSuffix;
    const body = {
      updateId,
      revisionId: ResourceRestService.getResourcesString(Resources.RequestedRevision)
    } as any;
    body[visibleIn === 'report' ? 'includeInReport' : 'includeInMI'] = visible;

    return this._http.post(url, body).pipe(
      retry(2),
      map(res => res.json()),
      catchError(() => {
        return Observable.empty();
      })
    );
  }

  public getTrials(): Observable<any> {
    return this._http.get(TherapiesService.trialsURLSuffix, { context: ResourceRestService.getResourcesString(Resources.ClinicalTrialMatch, Resources.RequestedRevision) })
      .map(res => res.json())
      .catch(err => {
        this.notificationMessageService.error(err.error || 'unable to get trials');
        return Observable.empty();
      });
  }

  public getTrialsWithAberrationsAsAggregateChildren(): Observable<any[]> {
    const requestQuery = (revisionId): DynamicSearchPayload => ({
      'filters': [
        {
          'path': '{context}',
          'type': 'reference',
          'value': revisionId,
          'operator': '=',
          'resource': 'ClinicalTrialMatch'
        },
        {
          'path': '{context}',
          'type': 'reference',
          'value': revisionId,
          'operator': '=',
          'resource': 'Aberration'
        }
      ],
      'sort': [
        {
          'path': '{meta,created}',
          'resource': 'ClinicalTrialMatch',
          'direction': 'DESC',
          'type': 'simplestring'
        }],
      'relations': [
        {
          'cardinality': 'left',
          '_fieldslist': 'AberrationClass,AberrationType,Canonical_AAC_1l,Canonical_HGVS_Protein,Canonical_HGVS_Coding,Canonical_HGVS_C,affected_exons,ckb_specific_variants,gene_name,HGNC_gene,gene_name1,gene_name2,gene,CopyNumber,Left_Breakpoint,Right_Breakpoint,affected_exons,SomaticStatus',
          'parent': 'ClinicalTrialMatch',
          'path': '{patientData,patientBiomarkers,id}',
          '_count': 'ALL',
          'child': 'Aberration',
          'direction': '_include'
        },
        {
          'cardinality': 'left',
          '_fieldslist': 'TMB,MSI,PDL1_text,msiSites,WTCandidates,msiSitesUnstable,nonsynonymousTmb,pctUnstableSites,codingRegionSizeMb,numberPassingEligibleVariants,numberPassingEligibleNonsynonymousVariants',
          'parent': 'ClinicalTrialMatch',
          'path': '{patientData,patientBiomarkers,id}',
          '_count': 'ALL',
          'child': 'MolecularFinding',
          'direction': '_include'
        },
      ],
      'target': {
        '_page': 0,
        '_count': 'ALL',
        '_forceFilter':true,
        '_forceTargetPage':true
      }
    });

    return this.workflowConfig.getRequestedRevisionId().pipe(
      switchMap(revisionId =>
        this._dynamicSearchService.sendAggregationDataQuery('clinicalTrialMatch', requestQuery(revisionId))
      ),
      take(1), // probably httpService issue with not closing subjects
      map((bundleResponse: Array<any>) => this.addAberrationsFromBiomarkers(bundleResponse)),
      catchError((err) => {
        this.notificationMessageService.error(err.error || 'unable to get trials');
        return Observable.empty();
      })
    );
  }

  public updateClinicalRecommendation(recommendation: any, testId: string): Observable<any> {
    return this._http.put(`${TherapiesService.clinicalRecommendationsURLSuffix}/${recommendation.id}?testId=${testId}`, recommendation, true)
      .pipe(
        map(data => {
          this.notificationMessageService.success('Clinical recommendation updated successfully!');
          return data.json();
        }),
        catchError(err => {
          this.notificationMessageService.error('Failed to update clinical recommendation');
          return Observable.throw(err);
        }));
  }

  /*
  * Warn: mutates the origin!
  * */
  public addAberrationsFromBiomarkers<T extends IResourceWithBioMarkers>(resources: T[] /*IClinicalRecommendation[] | IclinicalTrials[]*/): T[] {
    resources.forEach(resource => {
      const aberrationMap: { [id: string]: { Aberration: { id: string, [key: string]: any}}} = { };
      const molecularFindingFirstChild = resource._aggregatedChildren.find(child => !!child['MolecularFinding']);

      resource.patientData?.patientBiomarkers?.forEach(biomarker => {
        if (biomarker.aberrationType) {
          biomarker.AberrationType = biomarker.aberrationType;
        }

        if (biomarker.aberrationClass) {
          biomarker.AberrationClass = biomarker.aberrationClass;
        }

        if (biomarker.AberrationType && molecularFindingFirstChild) { // in some cases, AberrationType may be absent
          if (biomarker.AberrationType.toLowerCase() === 'wild-type') {
            const candidateGene = molecularFindingFirstChild.MolecularFinding.WTCandidates
              .find(wtCandidate => wtCandidate.gene === biomarker.gene);

            const aberrationId = `${molecularFindingFirstChild.MolecularFinding.id}-${candidateGene}`;
            aberrationMap[aberrationId] = {
              Aberration: {
                ...molecularFindingFirstChild.MolecularFinding,
                id: aberrationId,
                WTCandidate: candidateGene,
              }
            };
          } else if (biomarker.AberrationClass.toLowerCase() === 'molecularfinding') {
            const candidateGene = (biomarker.gene === 'PD-L1' || biomarker.gene === 'PDL1') ? 'PDL1_text' : biomarker.gene;

            // the condition for fix the GEN-86623
            if (get(molecularFindingFirstChild.MolecularFinding, 'TMB.edit.level', false) === 'Unspecified') {
              molecularFindingFirstChild.MolecularFinding.TMB.edit.level = biomarker.AberrationType;
            }

            const aberrationId = `${molecularFindingFirstChild.MolecularFinding.id}-${biomarker.gene}`;
            aberrationMap[aberrationId] = {
              Aberration: {
                ...molecularFindingFirstChild.MolecularFinding,
                id: aberrationId,
                molecularFindingCandidate: candidateGene,
              }
            };
          }
        }
      });

      resource._aggregatedChildren = [...resource._aggregatedChildren, ...Object.values(aberrationMap)];
    });

    return resources;
  }

  private assignReferencesToClinicalRecommendation(clinicalRec: any) {
    let references = { };

    if (clinicalRec.therapy) {
      references = Object.assign(references, this.extractReferences(clinicalRec.therapy));
      if (clinicalRec.therapy.drugs) {
        references = Object.assign(references, this.extractReferences(clinicalRec.therapy.drugs));
      }
    }

    if (clinicalRec.patientData && clinicalRec.patientData.patientIndications) {
      references = Object.assign(references, this.extractReferences(clinicalRec.patientData.patientIndications));
    }

    if (clinicalRec.clinicalEvidence) {
      references = Object.assign(references, this.extractReferences(clinicalRec.clinicalEvidence));
    }

    if (clinicalRec.requiredCondition) {
      if (clinicalRec.requiredCondition.requiredIndications) {
        references = Object.assign(references, this.extractReferences(clinicalRec.requiredCondition.requiredIndications));
      }

      if (clinicalRec.requiredCondition.requiredBiomarkers) {
        references = Object.assign(references, this.extractReferences(clinicalRec.requiredCondition.requiredBiomarkers));
        clinicalRec.requiredCondition.requiredBiomarkers.forEach(biomarker => {
          if (clinicalRec.gene) {
            references = Object.assign(references, this.extractReferences(clinicalRec.gene));
          }
        });
      }
    }

    clinicalRec['_references'] = Object.values(references);
    clinicalRec['_references'] = _sortBy(clinicalRec['_references'], [(ref => ref.reference.authors)]);

    if (clinicalRec.therapy) {
      const authorities = { };
      if (Array.isArray(clinicalRec.therapy.registeredFor)) {
        clinicalRec.therapy.registeredFor.forEach(registeredFor => {
          if (!Array.isArray(authorities[registeredFor.authority])) {
            authorities[registeredFor.authority] = [];
          }
          authorities[registeredFor.authority].push(registeredFor.indication.display);
        });
      }
      clinicalRec.therapy['_authorities'] = Object.entries(authorities)
        .map((v: [string, any]) => {
          let registeredIndicationValue = [];

          if (Array.isArray(v[1])) {
            registeredIndicationValue = v[1].filter((item, idx) => v[1].indexOf(item) === idx);
          }

          return {
            authority: v[0],
            registeredIndication: registeredIndicationValue
          };
        });
    }
    return clinicalRec;
  }

  private fetchClinicalRecommendations(query, sortConditions) {
    return this._dynamicSearchService.sendAggregationDataQuery('clinicalRecommendation', query).pipe(
      map(clinicalRecommendations => {
        if (sortConditions) {
          return clinicalRecommendations.sort((rec1, rec2) => this.compareRecommendationsAgainstConditions(sortConditions.conditions, rec1, rec2));
        } else {
          return clinicalRecommendations;
        }
      }),
      map(clinicalRecommendations =>
        clinicalRecommendations.map(clinicalRec =>
          this.assignReferencesToClinicalRecommendation(clinicalRec)
        )
      ),
      map(clinicalRecommendations => this.addAberrationsFromBiomarkers(clinicalRecommendations))
    );
  }

  private fetchDescendantTypesResources(requestedRevisionId: string, revisionIds: string[]): Observable<{ Aberration: string, MolecularFinding: string, }> {
    return this._workflowService.getWorkflowRevisionDescendantTypes(revisionIds).pipe(
      map(descendantTypesResponse => {
        const descendantTypes = get(descendantTypesResponse, 'descendant_types_per_workflowrequest[0].descendant_types', []);
        const descendantTypeWithAberration = descendantTypes.find(dt => dt.descendant_resource_type === 'Aberration');
        const Aberration = descendantTypeWithAberration?.workflowrevision_id || requestedRevisionId;

        return {
          MolecularFinding: requestedRevisionId,
          Aberration,
        };
      })
    );
  }

  // warn: mutates resourceQuery
  public getClinicalRecommendations(resourceQuery?: any, sortConditions?: any): Observable<any> {
    const defaultQuery = (revisionId): DynamicSearchPayload => ({
      'filters': [
        {
          'path': '{context}',
          'type': 'reference',
          'value': revisionId,
          'operator': '=',
          'resource': 'ClinicalRecommendation'
        },
      ],
      'sort': [
        {
          'path': '{meta,created}',
          'resource': 'ClinicalRecommendation',
          'type': 'simplestring',
          'direction': 'DESC'
        }
      ],
      'relations': [
        {
          'cardinality': 'left',
          '_fieldslist': 'AberrationClass,AberrationType,Canonical_AAC_1l,Canonical_HGVS_Protein,Canonical_HGVS_Coding,Canonical_HGVS_C,affected_exons,ckb_specific_variants,gene_name,HGNC_gene,gene_name1,gene_name2,gene,CopyNumber,Left_Breakpoint,Right_Breakpoint,affected_exons',
          'parent': 'ClinicalRecommendation',
          'path': '{patientData,patientBiomarkers,id}',
           '_count': 'ALL',
          'child': 'Aberration',
          'direction': '_include'
        },
        {
          'cardinality': 'left',
          '_fieldslist': 'TMB,msiSites,WTCandidates,msiSitesUnstable,nonsynonymousTmb,pctUnstableSites,codingRegionSizeMb,numberPassingEligibleVariants,numberPassingEligibleNonsynonymousVariants',
          'parent': 'ClinicalRecommendation',
          'path': '{patientData,patientBiomarkers,id}',
           '_count': 'ALL',
          'child': 'MolecularFinding',
          'direction': '_include'
        },
      ],
      'target': {
        '_page': 0,
        '_count': 'ALL',
        '_forceFilter':true,
        '_forceTargetPage':true
      }
    });

    return combineLatest([
      this.workflowConfig.getActiveWorkflow(),
      this.workflowConfig.getRequestedRevisionId(),
    ]).pipe(
      switchMap(([activeWorkflow, requestedRevisionId]) => {
        let isFilterWithAberrationFound = false;
        resourceQuery?.filters.forEach(_filter => {
          if (_filter.path === '{context}' && _filter.type === 'reference') {
            _filter.value = requestedRevisionId;
            isFilterWithAberrationFound = isFilterWithAberrationFound || (_filter.resource === 'Aberration');
          }
        });

        if (isFilterWithAberrationFound) {
          const revisionIds = activeWorkflow.revisions.map(revision => revision.id);
          return this.fetchDescendantTypesResources(requestedRevisionId, revisionIds).pipe(
            tap(descendantTypes => {
              resourceQuery.filters.forEach(_filter => {
                if (_filter.path === '{context}' && _filter.type === 'reference' && _filter.resource === 'Aberration') {
                  _filter.value = descendantTypes.Aberration;
                }
              });
            }),
            map(() => requestedRevisionId),
          );
        }
        return Observable.of(requestedRevisionId);
      }),
      switchMap(requestedRevisionId =>
        this.fetchClinicalRecommendations(resourceQuery || defaultQuery(requestedRevisionId), sortConditions)
      ),
    );

  }

  public compareRecommendationsAgainstConditions(conditions: IConditionalItem[], clinicalRecommendationOne: any, clinicalRecommendationTwo: any) {
    let localRes = 0;
    let sortCriterion;

    for (let i = 0; i < conditions.length; i++) {
      sortCriterion = conditions[i];
      const valueFromRec1 = jmespath.search(clinicalRecommendationOne, sortCriterion.path);
      const valueFromRec2 = jmespath.search(clinicalRecommendationTwo, sortCriterion.path);

      const rec1ConditionsPass = ConditionalItem.builder(valueFromRec1, sortCriterion.value, sortCriterion.operator).verify();
      const rec2ConditionsPass = ConditionalItem.builder(valueFromRec2, sortCriterion.value, sortCriterion.operator).verify();

      if (rec1ConditionsPass === rec2ConditionsPass) { localRes = 0; }
      else {
        localRes = rec1ConditionsPass ? -1 : 1;
        return localRes;
      }
    }
    return localRes;
  }

  private extractReferences(source): { [key: string]: any} {
    if (Array.isArray(source)) {
      return this.extractReferencesFromArray(source);
    }

    const references = { };
    if (source.narratives) {
      source.narratives.forEach(narrative => {
        if (narrative.citations) {
          narrative.citations.forEach(citation => {
              if (citation.source && citation.authors) { // only reference with authors needed
                references[citation.source] = { reference: citation};
              }
          });
        }
      });
    }
    if (source.citations) {
      source.citations.forEach(citation =>  {
          if (citation.source && citation.authors) { // only reference with authors needed
            references[citation.source] = { reference: citation};
          }
      });
    }
    return references;
  }

  private extractReferencesFromArray(source) {
    let references = { };
      source.forEach(sourceItem => {
        references = Object.assign(references, this.extractReferences(sourceItem));
      });
    return references;
  }

  public getTrialsByAberrationId(aberrationId) {
    return this._http.get(TherapiesService.trialsURLSuffix, { context: aberrationId })
      .map(res => res.json())
      .catch(err => {
        this.notificationMessageService.error(err.error || 'unable to get trials');
        return Observable.empty();
      });
  }

  public getPriority() {
    return this.workflowConfig.getRequestedRevisionId().pipe(
      switchMap(revisionId =>
        this._http.get(`${TherapiesService.therapiesURLSuffix}/getPriority`, { context: revisionId }, false)
      ),
      map(res => res.json()),
    );
  }

  public savePriority(priorityObject): Observable<any> {
    return this._http.post(`${TherapiesService.therapiesURLSuffix}/savePriority`, priorityObject, true)
      .map(res => {
        res = res.json();
        this.notificationMessageService.success('priority saved');
        return res;
      })
      .catch(err => {
        this.notificationMessageService.error(err.error || 'unable to save priority');
        return Observable.empty();
      });
  }

  public updatePriority(priorityObject): Observable<any> {
    return this._http.put(`${TherapiesService.therapiesURLSuffix}/updatePriority`, priorityObject, true)
      .map(res => {
        res = res.json();
        this.notificationMessageService.success('priority updated');
        return res;
      })
      .catch(err => {
        this.notificationMessageService.error(err.error || 'unable to save priority');
        return Observable.empty();
      });
  }

  public signCase(activeRevision, workflowrequest_id, practitioner): Promise<any> {
    return this._workflowService.reassignTest(
      {
        activeRevision,
        workflowrequest_id
      },
      practitioner,
      false
    ).catch(err => {
      console.error(err.error);
      const action = practitioner.toLowerCase === 'none' ? 'unassign' : 'accept';
      this.notificationMessageService.error(`unable to ${action} case`);
    });
  }

  public signOff(analysis, step, targetPractitionerId, revisionId, workflowId, interpretationText): Observable<any> {
    const params = {
      analysis: analysis,
      targetPractitioner: targetPractitionerId,
      targetStep: step,
      revisionId: revisionId,
      workflowId: workflowId,
      interpretationText: interpretationText
    };
    return this._http.post(`${TherapiesService.therapiesURLSuffix}/signOff`, params, false);
  }

  public getReflexMessageForUi(revisionId: string): Observable<any> {
    return this._http.get(`${TherapiesService.therapiesURLSuffix}` + TherapiesService.reflexMessageForUi, { revisionId: revisionId }, false)
      .map(res => res.json().message)
      .share()
      .catch(err => {
        this.notificationMessageService.error(err.error || 'reflexMessageForUi failed');
        return of('');
      });
  }

  public runAction(action: (payload?: any) => void, modalConfig?: IModalConfiguration, destroyer$?: Observable<any>) {
    const response = modalConfig ? this.showModal(modalConfig, destroyer$) : of(modalResponse.approve);

    response.pipe(take(1))
      .subscribe((res: modalResponse) => {
        switch (res) {
          case modalResponse.approve:
            action();
            break;
        }
      });
  }

  public showModal(config: IModalConfiguration, destroyer$: Observable<any> = Observable.create()) {
    return this._dynamicModalLoader.popupModal(ConfirmModalComponent,
      ConfirmModalLoaderPayload.builder()
        .setTitle(config.title)
        .setApproveButtonText(config.mainButton)
        .setDeclineButtonText(config.secondaryButton),
      ModalTypes.ALERT,
      TherapiesService.ModalLoaderTunnelName
    ).pipe(
      takeUntil(destroyer$),
      filter(response => response.payload),
      pluck('payload')
    );
  }

  public destroyModal(modalLoaderTunnelName, shouldEmit, emitterType: EmitterTypes = EmitterTypes.parent, emitType: EmitType = EmitType.Dismiss, data: any = null) {
    const payloadData = shouldEmit ? new TunnelResult(emitterType, emitType, data) : null;
    this._dynamicModalLoader.dismissObservable(modalLoaderTunnelName, shouldEmit, payloadData);
  }

  public updateTherapyMatch(therapy: ItherapyMatch, testId: string) {
    therapy.drugs.forEach(drug => {
      delete drug.displayName;
      delete drug.aberration;
      delete drug.gene;
    });
    return this._http.put(`/therapymatch/${therapy.id}?testId=${testId}`, therapy, true).pipe(
      map(therapy => {
        this.notificationMessageService.success('Therapy updated successfully!');
        return therapy.json();
      }),
      catchError(err => {
        this.notificationMessageService.error('Failed to update therapy');
        return Observable.throw(err);
      })
    );
  }

  public filterTherapiesByCondition(extendedTab: IExtendedTab) {
    //Going over all tabs and all the panels inside the tabs, filters their data and calcualtes the amount
    extendedTab.panels.forEach(panel => {
      panel.filteredData = panel.filteredData.filter(item =>
        (panel.conditions || [])
          .every(condition => ConditionalItem.builder(item, condition.value, condition.operator, condition.path).verify())
      &&
        (!panel.additionalData?.resourceMap ||
          panel.additionalData?.resourceMap?.find(resource =>
            resource.conditions
              ? resource.conditions.every(condition => ConditionalItem.from(item, condition).verify())
              : true
          )
        )
      );

      if (panel.type === 'form') {
        const resource = panel.filteredData[0] || { };
        panel.filteredData = [{ ...resource, ...{
            id: StringUtils.generateGuid(),
            resourceType: get(resource, 'resourceType') || 'ManualEntry',
            listData: resource,
          }}];
      }
      panel.amount = panel.filteredData.length;
    })
    extendedTab.amount = extendedTab.panels.reduce((current, _panel) => (current += _panel.amount || 0), 0);
  }
}
